import { z } from 'zod';

export const listContentsRequestSchema = z.object({
  type: z.enum(['Banner', 'Faq', 'Onboarding', 'ExploreApp', 'LegalText', 'Blog', 'Campaign']).optional(),
  name: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  status: z.enum(['Passive', 'Active', 'Deleted']).optional(),
  pageNumber: z.number().optional(),
  pageSize: z.number().optional(),
  OrderColumn: z.string().optional(),
  OrderType: z.string().optional(),
});

export const listContentsResponseSchema = z.object({
  totalCount: z.number(),
  result: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      type: z.enum(['Banner', 'Faq', 'ExploreApp', 'LegalText', 'Blog', 'Onboarding', 'Campaign']),
      rank: z.number(),
      startDate: z.string().nullable(),
      endDate: z.string().nullable(),
      status: z.enum(['Passive', 'Active', 'Deleted']),
      createdDate: z.string(),
      updatedDate: z.string().nullable(),
    })
  ),
});

export type ListContentsRequest = z.infer<typeof listContentsRequestSchema>;
export type ListContentsResponse = z.infer<typeof listContentsResponseSchema>;

export const updateContentStatusRequestSchema = z.object({
  status: z.enum(['Passive', 'Active']),
});

export const updateContentStatusResponseSchema = z.any();

export type UpdateContentStatusRequest = z.infer<typeof updateContentStatusRequestSchema>;
export type UpdateContentStatusResponse = z.infer<typeof updateContentStatusResponseSchema>;

export const rankingItemSchema = z.object({
  id: z.string(),
  rank: z.number(),
});

export const updateContentRankingRequestSchema = z.array(rankingItemSchema);

export const updateContentRankingResponseSchema = z.any();

export type RankingItem = z.infer<typeof rankingItemSchema>;
export type UpdateContentRankingRequest = z.infer<typeof updateContentRankingRequestSchema>;
export type UpdateContentRankingResponse = z.infer<typeof updateContentRankingResponseSchema>;

export const contentImageSchema = z.object({
  languageId: z.string(),
  document: z.any(), // File object
  environmentType: z.enum(['Mobile', 'Desktop']),
  id: z.number().optional(),
  IsDeleted: z.boolean().optional(),
});

export const createContentRequestSchema = z.object({
  name: z.string().optional(),
  title: z.record(z.string(), z.string()).optional(), // Multi-language title object
  description: z.record(z.string(), z.string()).optional(), // Multi-language description
  shortDescription: z.record(z.string(), z.string()).optional(), // Multi-language short description
  image: z.array(contentImageSchema).optional(),
  redirectUrl: z.string().optional(),
  type: z.enum(['Banner', 'Faq', 'ExploreApp', 'LegalText', 'Blog', 'Onboarding', 'Campaign']),
  startDate: z.string().optional(), // YYYY-MM-DD format
  endDate: z.string().optional(), // YYYY-MM-DD format
  status: z.enum(['Passive', 'Active']).optional(),
});

export const createContentResponseSchema = z.any();

export type ContentImage = z.infer<typeof contentImageSchema>;

export type CreateContentRequest = z.infer<typeof createContentRequestSchema>;
export type CreateContentResponse = z.infer<typeof createContentResponseSchema>;

export const contentDetailResponseSchema = z.object({
  name: z.string().nullable(),
  title: z.record(z.string(), z.string()).nullable(),
  description: z.record(z.string(), z.string()).nullable(),
  shortDescription: z.record(z.string(), z.string()).nullable(),
  imageUrl: z
    .array(
      z.object({
        languageId: z.string(),
        documents: z.array(
          z.object({
            id: z.number(),
            publicUrl: z.string(),
            environmentType: z.enum(['Mobile', 'Desktop']),
          })
        ),
      })
    )
    .nullable(),
  redirectUrl: z.string().nullable(),
  type: z.enum(['Banner', 'Faq', 'ExploreApp', 'LegalText', 'Blog', 'Onboarding', 'Campaign']),
  rank: z.number(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  lastViewDate: z.string().nullable().optional(),
  status: z.enum(['Passive', 'Active']),
});

export type ContentDetailResponse = z.infer<typeof contentDetailResponseSchema>;

export const updateContentRequestSchema = z.object({
  name: z.string().optional(),
  title: z.record(z.string(), z.string()).optional(), // Multi-language title object
  description: z.record(z.string(), z.string()).optional(),
  shortDescription: z.record(z.string(), z.string()).optional(), // Multi-language short description
  rank: z.number().nullable().optional(),
  image: z.array(contentImageSchema).optional(),
  redirectUrl: z.string().optional(),
  type: z.enum(['Banner', 'Faq', 'ExploreApp', 'LegalText', 'Blog', 'Onboarding', 'Campaign']),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  status: z.enum(['Passive', 'Active']).optional(),
});

export const updateContentResponseSchema = z.any();

export type UpdateContentRequest = z.infer<typeof updateContentRequestSchema>;
export type UpdateContentResponse = z.infer<typeof updateContentResponseSchema>;

export const deleteContentResponseSchema = z.any();

export type DeleteContentResponse = z.infer<typeof deleteContentResponseSchema>;

export type HeroBannerImage = ContentImage;

export type CreateHeroBannerRequest = CreateContentRequest;
export type CreateHeroBannerResponse = CreateContentResponse;
